import BaseController from "./BaseController";
import JSONModel from "sap/ui/model/json/JSONModel";
import { ErrorHandler } from "../service/ErrorHandler.service";
import { EnvironmentService } from "../service/Environment.service";
import formatter from "../model/formatter";
import encodeURL from "sap/base/security/encodeURL";
import { SearchField$SearchEvent } from "sap/m/SearchField";
import { ListBase$ItemPressEvent, ListBase$SelectionChangeEvent, ListBase$UpdateFinishedEvent, ListBase$UpdateStartedEvent } from "sap/m/ListBase";
import { Artikel, Preis } from "../model/models";
import { Button$PressEvent } from "sap/m/Button";
import Table from "sap/m/Table";
import { SettingsService } from "../service/Settings.service";
import { Route$PatternMatchedEvent } from "sap/ui/core/routing/Route";
import { CheckBox$SelectEvent } from "sap/m/CheckBox";

/**
 * @namespace com.unitrade.crm.controller
 */
export default class ArticleList extends BaseController {
  private _settingsService: SettingsService;
  private _oModel: JSONModel;

  public override async getTitle(): Promise<string> {
    return await this.getTranslatedString("ArticleSearch");
  }

  public async onInit(): Promise<void> {
    this._oModel = new JSONModel({
      search: ""
    });
    this._settingsService = new SettingsService();
    this._oModel.setProperty("/isUsingSlowDatabaseSearch", false);
    
    this.getRouter().getRoute("articleList").attachPatternMatched(this._onPatternMatch);
    this.setModel(this._oModel);
  }

  private _onPatternMatch = async (oEvent: Route$PatternMatchedEvent) => {
    const userConfig = this._settingsService.getUserConfiguration();

    if(userConfig) {
      this._oModel.setProperty("/commissionedGoodsEnabled", userConfig.commissionedGoodsEnabled);
      this._oModel.setProperty("/showCommissionGoods", false); // Kommissionsware per Default nicht anzeigen
    }
  };

  protected onSearch = async (oEvent: SearchField$SearchEvent) => {

    this._loadArtikel();
  };

  private resetSelection = () => {
    const model = this.getModel() as JSONModel;
    if (model) {
      model.setProperty("/selectedItems", []);
    }

    const oTable = this.byId("artikelTable") as Table;
    if (oTable) {
      oTable.removeSelections(true);
    }
  };

  private async _loadArtikel(): Promise<void> {
    this._oModel.setProperty("/artikel", []);
    this.resetSelection();

    const searchtext = this._oModel.getProperty("/search");
    if(searchtext == null || searchtext.length <= 1) {
      return;
    }

    this._oModel.setProperty("/busy", true);
    let query = `${EnvironmentService.getBaseUrl()}/artikel/find/${encodeURL(searchtext)}`;
    const commissionGoodsEnabled = this._oModel.getProperty("/commissionedGoodsEnabled") as Boolean;
    const loadCommissionGoods = (this._oModel.getProperty("/showCommissionGoods") as Boolean) 
                             && (commissionGoodsEnabled);
    if (commissionGoodsEnabled) {
      query = query += `?ladeKommissionsWare=${loadCommissionGoods}`
    }

    jQuery.get(query).then(
      (newArticle: Artikel[], textStatus, jqXHR) => {
        const searchSource = jqXHR.getResponseHeader("x-source");
        if (searchSource === "Database") {
          this._oModel.setProperty("/isUsingSlowDatabaseSearch", true);
        } else {
          this._oModel.setProperty("/isUsingSlowDatabaseSearch", false);
        }

        this._oModel.setProperty("/artikel", newArticle);
        this._oModel.setProperty("/busy", false);
      },
      (fail) => {
        ErrorHandler.handleError(fail, { title: "Fehler bei der Artikelsuche", ignorable: true, onRetry: () => this._loadArtikel() });
      }
    );
  }

  private _loadArtikelPreis(artid: number) {
    jQuery.get({
      url: `${EnvironmentService.getBaseUrl()}/artikel/${artid}/preis`,
      success: (data: Preis, textStatus, jqXHR) => {
        const model = this.getModel() as JSONModel;
        const artikelList = model.getProperty("/artikel") as Artikel[];
        const artikelIndex = artikelList.findIndex((art) => art.id === artid);
        model.setProperty("/artikel/" + artikelIndex + "/preis", data.gesamtPreisNetto);
        model.setProperty("/artikel/" + artikelIndex + "/preisMeasure", "EUR");
      },
    });
  }

  protected onListItemPress = (oEvent: ListBase$ItemPressEvent) => {
    const oItem = oEvent.getSource();
    const oBindingContext = oItem.getBindingContext();
    const oModel = oBindingContext.getModel();
    const id = oModel.getProperty("id", oBindingContext) as number;
    this.getOwnerComponent().getRouter().navTo("articleDetails", { idPath: id });
  };

  protected onSelection = (oEvent: ListBase$SelectionChangeEvent) => {
    const model = this.getModel() as JSONModel;
    model.setProperty(
      "/selectedItems",
      oEvent
        .getSource()
        .getSelectedContexts()
        .map((context) => context.getObject() as Artikel)
    );
  };

  protected compareButtonTextFormatter(arr: []) {
    const count = !arr ? 0 : arr.length;

    return `Vergleichen(${count})`;
  }

  protected compareVisibleFormatter(arr: []) {
    const count = !arr ? 0 : arr.length;

    return count > 1;
  }

  protected onComparePress = (oEvent: Button$PressEvent) => {
    const model = this.getModel() as JSONModel;
    const selectedArtikel = model.getProperty("/selectedItems") as Artikel[];
    const selectedArtikelIds = selectedArtikel.map((art) => art.id);
    this.getOwnerComponent()
      .getRouter()
      .navTo("articleCompare", { idsPath: selectedArtikelIds.join(",") });
  };

  protected onShowCommissionGoodsSelect = (oEvent: CheckBox$SelectEvent) => {
    this._loadArtikel()
  }

  protected artikelWithDispoFormatter = (dispokz: string, bereich: number, warengruppe: number, artikelgruppe: number, artikelnummer: number) => {
    let formattedNumber = formatter.artikelNummer(bereich, warengruppe, artikelgruppe, artikelnummer);
    if(this._oModel.getProperty("/commissionedGoodsEnabled")) {
      formattedNumber = `(${dispokz}) ${formattedNumber}`;
    }

    return formattedNumber;
  }
}
